import { apiCweb } from './api-cweb';
import {
  DbCommit,
  DbFolder,
  DbFolderProperties,
  DbFocusOptions,
  DbID,
  DbProject,
  DbTenderFolder,
  DbTender,
  DbFile,
} from '~src/types/db';
import { appLog } from '~src/utils/log';
import { DB_STATE } from '~src/types/enums';
import { sortBy } from 'lodash';

export interface LoadFoldersParams {
  projectId: string;
  favourites?: boolean;
  before?: number;
  after?: number;
  search_name?: string;
  search_description?: string;
  as_admin?: boolean;
  show_deleted?: boolean;
  root_folder_id?: string;
  folder_id?: string;
  tender_id?: string;
  tender_id_or_unassigned?: string;
}
export const loadFolders = async (
  key,
  {
    projectId,
    favourites,
    before,
    after,
    search_name,
    search_description,
    as_admin,
    show_deleted,
  }: LoadFoldersParams
) => {
  appLog('loadFolders ->', { projectId, favourites });
  const res = await apiCweb.get<DbFolder[]>(`/projects/${projectId}/folders`, {
    params: {
      favorites: favourites,
      before: before,
      search_name: search_name,
      search_description: search_description,
      as_admin: as_admin,
      show_deleted: show_deleted === undefined ? as_admin : show_deleted,
      after: after,
    },
  });

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

interface CreateFolderParams {
  projectId: string;
  folderData: Pick<
    DbFolder,
    'name' | 'description' | 'parent_id' | 'is_locked' | 'state'
  > &
    Partial<DbFolder>;
  commit_id?: DbCommit['id'];
}

interface CreateTenderParams {
  projectId: string;
  folderData: Pick<DbTenderFolder, 'name' | 'description'> &
    Partial<DbTenderFolder>;
  commit_id?: DbCommit['id'];
}

export const createFolder = async ({
  projectId,
  folderData,
  commit_id,
}: CreateFolderParams) => {
  appLog('createFolder ->', { projectId, folderData });
  const res = await apiCweb.post<DbFolder>(
    `/projects/${projectId}/folders`,
    folderData,
    { params: { commit_id: commit_id } }
  );
  return res.data;
};

interface EditFolderParams {
  projectId: string;
  folderData: DbFolder;
}
export const editFolder = async ({
  projectId,
  folderData,
}: EditFolderParams) => {
  appLog('editFolder ->', { projectId, folderData });
  const res = await apiCweb.put<DbFolder>(
    `/projects/${projectId}/folders/${folderData.id}`,
    folderData
  );
  return res.data;
};

interface DeleteFolderParams {
  projectId: string;
  folder: DbFolder;
  commitId?: DbCommit['id'];
}
export const deleteFolder = async ({
  projectId,
  folder,
}: DeleteFolderParams) => {
  appLog('deleteFolder ->', { projectId, folder });
  const res = await apiCweb.delete<DbFolder>(
    `/projects/${projectId}/folders/${folder.id}`
  );

  return res.data;
};

interface LoadFolderPropertiesParams {
  projectId: string;
  folderId: string;
}

export const loadFolderProperties = async (
  key,
  { projectId, folderId }: LoadFolderPropertiesParams
): Promise<DbFolderProperties> => {
  appLog('loadFolderProperties ->', { projectId, folderId });
  const res = await apiCweb.get<DbFolderProperties>(
    `/projects/${projectId}/folder_properties`,
    { params: { root_id: folderId } }
  );

  return res.data;
};

interface ToggleFolderNotifyParams {
  notify: boolean;
  recursive: boolean;
  projectId: DbProject['id'];
  folderId: DbFolder['id'];
}

export const toggleFolderNotify = async ({
  notify,
  recursive,
  projectId,
  folderId,
}: ToggleFolderNotifyParams) => {
  appLog('toggleFolderNotify -> ', {
    notify,
    recursive,
    projectId,
    folderId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/folders/${folderId}/notify`,
    {
      is_notify: notify,
      is_recursive: recursive,
    }
  );

  return res.data;
};

interface FocusFolderParams {
  projectId: string;
  folderId: DbFolder['id'];
  focustValue?: DbFolder['focus'];
}

export const editFocusOptions = async ({
  projectId,
  folderId,
  focustValue,
}: FocusFolderParams) => {
  const res = await apiCweb.put(
    `/projects/${projectId}/folders/${folderId}/focus/${focustValue}`,
    {}
  );

  return res.data;
};

export const removeFocusOptions = async ({
  projectId,
  folderId,
}: FocusFolderParams) => {
  const res = await apiCweb.delete(
    `/projects/${projectId}/folders/${folderId}/focus`,
    {}
  );

  return res.data;
};

export const loadTenderFolders = async (
  key,
  {
    projectId,
    favourites,
    before,
    after,
    search_name,
    search_description,
    as_admin,
    show_deleted,
    tender_id_or_unassigned,
  }: LoadFoldersParams
) => {
  appLog('loadTenderFolders ->', { projectId, favourites });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tender_folders`,
    {
      params: {
        favorites: favourites,
        before: before,
        search_name: search_name,
        search_description: search_description,
        as_admin: as_admin,
        show_deleted: show_deleted === undefined ? as_admin : show_deleted,
        after: after,
        tender_id_or_unassigned,
      },
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

export const loadTenderRegistartionSubFolders = async (
  //key,
  { projectId, favourites, root_folder_id }: LoadFoldersParams
) => {
  appLog('loadTenderSubFolders ->', { projectId, favourites });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tenders/${root_folder_id}/tender_folders`,
    {
      params: {},
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

export const loadTendersTenderFoldersFolders = async (
  key,
  { projectId, favourites, root_folder_id, folder_id }: LoadFoldersParams
) => {
  appLog('loadTendersTenderFoldersFolders ->', { projectId, favourites });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tenders/${root_folder_id}/tender_folders/${folder_id}/folders`,
    {
      params: {},
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

export const loadTenderFoldersFoldersId = async (
  key,
  { projectId, favourites, root_folder_id, tender_id }: LoadFoldersParams
) => {
  appLog('loadTenderFoldersFoldersId ->', { projectId, favourites });

  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tenders/${tender_id}/tender_folders/${root_folder_id}/folders`
  );

  return res.data;
};

export const loadRestLevelsTenderFolders = async ({
  projectId,
  favourites,
  root_folder_id,
  folder_id,
  tender_id,
}: LoadFoldersParams) => {
  appLog('loadRestLevelsTenderFolders ->', { projectId, favourites });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tenders/${tender_id}/tender_folders/${root_folder_id}/folders/${folder_id}`
  );

  return res.data;
};

export const loadTenderTenderFoldersVersionedFiles = async (
  key,
  {
    projectId,
    favourites,
    root_folder_id,
    folder_id,
    tender_id,
  }: LoadFoldersParams
) => {
  appLog('loadTenderTenderFoldersVersionedFiles ->', { projectId, favourites });
  const res = await apiCweb.get<DbFile[]>(
    `/projects/${projectId}/tenders/${tender_id}/tender_folders/${root_folder_id}/folders/${folder_id}/versioned_files`,
    { params: {} }
  );

  return res.data;
};

export const loadProjectTenderFoldersVersionedFiles = async (
  key,
  { projectId, root_folder_id, folder_id }: LoadFoldersParams
) => {
  appLog('loadProjectTenderFoldersVersionedFiles ->', {
    projectId,
    root_folder_id,
    folder_id,
  });

  const res = await apiCweb.get<DbFile[]>(
    `/projects/${projectId}/tender_folders/${root_folder_id}/folders/${folder_id}/versioned_files`,
    { params: {} }
  );

  return res.data;
};

export const loadTenders = async (
  key,
  {
    projectId,
    favourites,
    before,
    after,
    search_name,
    search_description,
    as_admin,
    show_deleted,
  }: LoadFoldersParams
) => {
  appLog('loadTenders ->', { projectId, favourites });
  const res = await apiCweb.get<DbTender[]>(`/projects/${projectId}/tenders`, {
    params: {
      favorites: favourites,
      before: before,
      search_name: search_name,
      search_description: search_description,
      as_admin: as_admin,
      show_deleted: show_deleted === undefined ? as_admin : show_deleted,
      after: after,
    },
  });

  const sorted = sortBy(res.data, [
    (folder) => (folder.title || '').toUpperCase(),
  ]);
  return sorted;
};

export const createTender = async ({
  projectId,
  folderData,
  commit_id,
}: CreateTenderParams) => {
  appLog('createFolder ->', { projectId, folderData });
  const res = await apiCweb.post<DbTenderFolder>(
    `/projects/${projectId}/tenders`,
    folderData,
    { params: { commit_id: commit_id } }
  );
  return res.data;
};
// /tender_folders?action=send_to_tender&folder_id=101

export const convertToTender = async ({
  projectId,
  folderId,
}: FocusFolderParams) => {
  appLog('convertToTender ->', { projectId, folderId });
  const res = await apiCweb.post<DbTenderFolder>(
    `/projects/${projectId}/tender_folders?action=send_to_tender&folder_id=${folderId}
`,
    {}
  );
  return res.data;
};

export const loadTenderSubFolders = async ({
  projectId,
  favourites,
  root_folder_id,
}: LoadFoldersParams) => {
  appLog('loadTenderSubFolders ->', { projectId, favourites });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tender_folders/${root_folder_id}/folders`,
    { params: {} }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};
